import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, FormLabel, FormControlLabel, Checkbox, Box } from '@mui/material';
import { GetInterestsList } from '../services/interests/InterestService';
import i18n from '../i18n';
import {
  Beauty,
  BeautyChecked,
  Baby,
  BabyChecked,
  Householdproducts,
  HouseholdproductsChecked,
  Health,
  HealthChecked,
  Photoservices,
  PhotoservicesChecked,
  Travel,
  TravelChecked,
} from './common/icons/InterestsCheckboxIcons';
import FamiliplusPointBox from './common/Forms/FamiliplusPointBox';
import { Interest } from './common/props-types';

const IconData = [
  {
    Id: 1,
    Name: 'Beauty',
    Icon: <Beauty />,
    CheckedIcon: <BeautyChecked />,
  },
  {
    Id: 2,
    Name: 'Baby',
    Icon: <Baby />,
    CheckedIcon: <BabyChecked />,
  },
  {
    Id: 3,
    Name: 'Householdproducts',
    Icon: <Householdproducts />,
    CheckedIcon: <HouseholdproductsChecked />,
  },
  {
    Id: 4,
    Name: 'Health',
    Icon: <Health />,
    CheckedIcon: <HealthChecked />,
  },
  {
    Id: 5,
    Name: 'Photoservices',
    Icon: <Photoservices />,
    CheckedIcon: <PhotoservicesChecked />,
  },
  {
    Id: 6,
    Name: 'Travel',
    Icon: <Travel />,
    CheckedIcon: <TravelChecked />,
  },
];

//todo: use unique static "code" fields instead of the ids
const GeneralInterestTypeId = 2;
const NoneInterestId = 7;

export const InterestsCheckbox = (props: {
  formik: any;
  setError: Dispatch<SetStateAction<string>>;
  showPtsBox: boolean;
  setInterestsSlug?: (value: string[] | ((prevVar: string[]) => string[])) => void;
}): JSX.Element => {
  const [interests, setInterests] = useState<Interest[]>([]);

  const SetFlsInterests = async () => {
    try {
      const getInterestsListResponse = await GetInterestsList(i18n.resolvedLanguage ?? '');
      const InterestResponse = await getInterestsListResponse.json();

      const generalType = InterestResponse.InterestType.find(
        (type: { Id: number }) => type.Id == GeneralInterestTypeId
      );
      const filteredInterests = generalType.Interests.filter(
        (interest: { Id: number }) => interest.Id != NoneInterestId
      );
      setInterests(filteredInterests);
    } catch (e) {
      props.setError('Errors.GenericError');
      return;
    }
  };

  useEffect(() => {
    SetFlsInterests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.resolvedLanguage]);

  useEffect(() => {
    if (props.setInterestsSlug && interests.length > 0 && props.formik.initialValues.interestsChecked.length > 0) {
      props.setInterestsSlug(
        interests
          .filter((interest) => props.formik.initialValues.interestsChecked.includes(interest.Id.toString()))
          .map((interest) => interest.Slug)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interests, props.formik.initialValues.interestsChecked]);

  return <Interests showPtsBox={props.showPtsBox} interests={interests} formik={props.formik} showOptionnal={true} />;
};

export const Interests = (props: {
  showPtsBox: boolean;
  interests: Interest[];
  formik: any;
  showOptionnal: boolean;
  setInterestsSlug?: (value: string[] | ((prevVar: string[]) => string[])) => void;
}): JSX.Element => {
  const { t } = useTranslation('component');

  const isChecked = (interestId: number): boolean => {
    return props.formik.values.interestsChecked.indexOf(interestId.toString()) > -1;
  };

  const onChange = (event: any) => {
    if (props.setInterestsSlug) {
      const slug = props.interests.find((interest) => interest.Id.toString() == event.target.value)?.Slug;
      if (slug) {
        props.setInterestsSlug((prevVar: string[]): string[] => {
          if (event.target.checked) {
            return [...prevVar, slug];
          } else {
            return [...prevVar.filter((val) => val != slug)];
          }
        });
      }
    }
    props.formik.handleChange(event);
  };

  return (
    <>
      <FormLabel id="InterestsCheckedLabel">
        {t('CommunicationPreferences.Interests')} {props.showOptionnal && t('Form.Optional')}
        {props.showPtsBox && <FamiliplusPointBox points="25"></FamiliplusPointBox>}
      </FormLabel>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr 1fr' },
          gap: 2,
          marginLeft: '-16px',
          marginRight: '16px',
        }}>
        {props.interests.map((interest) => (
          <FormControlLabel
            onChange={onChange}
            key={interest.Name}
            id={interest.Name.replaceAll(' ', '')}
            name="interestsChecked"
            value={interest.Id}
            checked={isChecked(interest.Id)}
            sx={{
              width: '100%',
              border: '1px solid #DFE3E6',
              borderRadius: '6px',
              padding: '6px',
              '&.Mui-checked': {
                backgroundColor: '#2757B4',
                color: '#fff',
              },
              justifyContent: 'center',
              textAlign: 'center',
            }}
            className={isChecked(interest.Id) ? 'Mui-checked' : ''}
            control={
              <Checkbox
                //todo: use a unique static "code" field instead of the id
                icon={IconData.find((x) => x.Id === interest.Id)?.Icon}
                checkedIcon={IconData.find((x) => x.Id === interest.Id)?.CheckedIcon}
              />
            }
            labelPlacement="bottom"
            label={
              <Typography component="span" sx={{ fontSize: '0.75rem' }}>
                {interest.Name}
              </Typography>
            }
          />
        ))}
      </Box>
    </>
  );
};
